import { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { hasFiles, pastedFiles } from '@components/attachments-tray/attachments';
import {
  IcoCamera,
  IcoChevronRight,
  IcoImage,
  IcoMicrophone,
  IcoVideoCamera,
} from '@components/icons';
import { ManagedFile } from '@components/media-manager';
import { useIntl } from 'shared/intl/use-intl';
import { RecentStrip } from './recent-strip';

export type CaptureMode = 'photo' | 'video' | 'audio';

type Props = {
  accept?: string;
  captures: CaptureMode[];
  allowEmbed?: boolean;
  onChooseFile(): void;
  onFiles(files: File[]): void;
  onCapture(mode: CaptureMode): void;
  onPick(file: ManagedFile): void;
  onBrowse(): void;
  onEmbed(): void;
};

function SourceButton({
  primary,
  onClick,
  children,
}: {
  primary?: boolean;
  onClick(): void;
  children: ComponentChildren;
}) {
  return (
    <button
      type="button"
      class={`inline-flex items-center justify-center gap-2 min-h-10 px-2.5 py-2 rounded-lg border text-sm font-medium whitespace-nowrap ${
        primary
          ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-500'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function HomeView(props: Props) {
  const intl = useIntl();
  const { onFiles } = props;
  const [isOver, setIsOver] = useState(false);
  const iconClass = 'size-[18px] shrink-0';
  const mutedIconClass = `${iconClass} text-gray-500`;

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (e.defaultPrevented) {
        return;
      }
      const files = pastedFiles(e.clipboardData);
      if (files.length) {
        e.preventDefault();
        onFiles(files);
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [onFiles]);

  return (
    <div
      class="p-6 overflow-auto"
      onDragOver={(e) => {
        if (hasFiles(e)) {
          e.preventDefault();
          setIsOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setIsOver(false);
        }
      }}
      onDrop={() => setIsOver(false)}
    >
      <section
        class={`rounded-xl border-[1.5px] border-dashed p-4 transition-colors ${
          isOver ? 'bg-indigo-50 border-indigo-500' : 'border-gray-300'
        }`}
        aria-labelledby="add-media-upload"
      >
        <h3 id="add-media-upload" class="text-sm font-semibold text-gray-900">
          {intl('Upload or record')}
        </h3>
        <p class="mt-0.5 mb-3 text-sm text-gray-500">{intl('Drag or paste a file here.')}</p>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <SourceButton primary onClick={props.onChooseFile}>
            <IcoImage class={iconClass} />
            {intl('Choose file')}
          </SourceButton>
          {props.captures.includes('photo') && (
            <SourceButton onClick={() => props.onCapture('photo')}>
              <IcoCamera class={mutedIconClass} />
              {intl('Take photo')}
            </SourceButton>
          )}
          {props.captures.includes('video') && (
            <SourceButton onClick={() => props.onCapture('video')}>
              <IcoVideoCamera class={mutedIconClass} />
              {intl('Record video')}
            </SourceButton>
          )}
          {props.captures.includes('audio') && (
            <SourceButton onClick={() => props.onCapture('audio')}>
              <IcoMicrophone class={mutedIconClass} />
              {intl('Record audio')}
            </SourceButton>
          )}
        </div>
      </section>
      <RecentStrip accept={props.accept} onPick={props.onPick} />
      <button
        type="button"
        class="mt-4 text-sm font-medium text-indigo-600 hover:underline"
        onClick={props.onBrowse}
      >
        {intl('Browse media library')} →
      </button>
      {props.allowEmbed && (
        <button
          type="button"
          class="mt-6 w-full flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left hover:border-indigo-500 hover:bg-indigo-50"
          onClick={props.onEmbed}
        >
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 text-sm font-bold"
            aria-hidden="true"
          >
            &lt;/&gt;
          </span>
          <span class="flex-1 min-w-0">
            <span class="block text-sm font-semibold text-gray-900">
              {intl('Embed a video or code')}
            </span>
            <span class="block text-sm text-gray-500">
              {intl('YouTube, Vimeo, Loom, Google Forms, or any embed code')}
            </span>
          </span>
          <IcoChevronRight class="size-[18px] text-gray-400" />
        </button>
      )}
    </div>
  );
}
